import React, { Component } from "react";
import TextArea from "antd/lib/input/TextArea";
import { Drawer, Form, Button, InputNumber, Modal, Row, Col } from "antd";
import { connect } from "dva";
import { showJiraWidget } from "@/utils/utils";
import MarginFeedbackAlert from "@/components/MarginFeedbackAlert";
import PricingEngineFeedbackForm from "@/components/PricingEngineFeedbackForm";

class CreateQuoteMenuItem extends Component {
  state = {
    isMobile: false,
    loading: false,
    usedPricingEngine: false,
    loadingPricingEngineResponse: false,
    usingSuggestedDealerPrice: false,
    usingSuggestedTransporterPrice: false,
    askingForFeedback: false,
    automatedPricingIsSafe: null,
    displayPricingEngine: false,
    ranAutoPricing: false,
    taqSuccess: null,
    currentDealerPrice: null,
    currentTransporterPrice: null,
    isLowPriceMargin: false,
    displayLowPriceMarginModal: false,
  };

  quotesFormRef = React.createRef();
  pricingEngineFeedBackFormRef = React.createRef();
  lowPriceMarginFormRef = React.createRef();

  componentDidMount = () => {
    let isMobile = window.innerWidth <= 768;
    this.setState({
      isMobile,
    });
  };

  // merge updated state into current state, function is passed to children
  setComponentState = (updatedState, callBack) => {
    this.setState(
      {
        ...updatedState,
      },
      callBack,
    );
  };

  // on price updates, update state for whether or not suggested prices are being used
  // doing this to set whether or not reason dropdowns are required to be changed from 'looks reasonable' when using pricing engine
  checkUpdatedPrices = () => {
    if (!this.quotesFormRef.current) return;

    const { dealerPrice: actualDealerPrice, transporterPrice: actualTransporterPrice } =
      this.quotesFormRef.current.getFieldsValue(["dealerPrice", "transporterPrice"]);

    this.setState({
      currentDealerPrice: actualDealerPrice,
      currentTransporterPrice: actualTransporterPrice,
    });

    if (!this.pricingEngineFeedBackFormRef.current) return;

    const { dealerPrice: suggestedDealerPrice, transporterPrice: suggestedTransporterPrice } =
      this.pricingEngineFeedBackFormRef.current.getFieldsValue(["dealerPrice", "transporterPrice"]);

    const usingSuggestedDealerPrice = Math.abs(actualDealerPrice - suggestedDealerPrice) <= 25;
    const usingSuggestedTransporterPrice = Math.abs(actualTransporterPrice - suggestedTransporterPrice) <= 25;

    this.setState({
      usingSuggestedDealerPrice,
      usingSuggestedTransporterPrice,
    });
  };

  // Calls to rules engine to determine if auto pricing is safe
  checkForSafeAutomatedPricing = vin => {
    const { dispatch, adminShipmentView } = this.props;
    const adminShipmentData = adminShipmentView.data;

    const { pickup, dropoff, entityId } = adminShipmentData;

    const origin = pickup.location.zip;
    const destination = dropoff.location.zip;
    const shipperEntityId = entityId?.toUpperCase();

    const rulesEnginePayload = {
      Workflow: "ValidTAQRequest",
      RuleSet: "ValidTAQRequest.json",

      Parameters: {
        origin,
        destination,
        shipperEntityId,
        vin,
      },
    };

    dispatch({
      type: `adminShipmentView/runRulesEngine`,
      payload: rulesEnginePayload,
    }).then(result => {
      if (result && !result?.errorMessage && !result?.errorType) {
        let parsedData = result;

        const evaluatedRules = parsedData;
        let allRulesPassed = true;

        // Check results of all rules evaluated, if any failed, then automated pricing is not safe
        for (let i = 0; i < evaluatedRules.length; i++) {
          if (!evaluatedRules[i].Success) {
            allRulesPassed = false;
            break;
          }
        }

        this.setState({
          automatedPricingIsSafe: allRulesPassed,
          ranAutoPricing: true,
        });
      } else {
        this.setState({
          automatedPricingIsSafe: null,
          ranAutoPricing: true,
        });
      }
    });
  };

  onClose = () => {
    const { hideQuoteCreateDrawer } = this.props;
    const { usedPricingEngine } = this.state;

    this.quotesFormRef.current.resetFields();
    if (usedPricingEngine) {
      this.pricingEngineFeedBackFormRef.current.resetFields();
    }
    this.setState({
      loading: false,
      usedPricingEngine: false,
      usingSuggestedDealerPrice: false,
      usingSuggestedTransporterPrice: false,
      askingForFeedback: false,
      automatedPricingIsSafe: null,
      displayPricingEngine: false,
      ranAutoPricing: false,
      taqSuccess: null,
      isLowPriceMargin: false,
      displayLowPriceMarginModal: false,
      currentDealerPrice: null,
      currentTransporterPrice: null,
    });
    hideQuoteCreateDrawer();
    showJiraWidget();
  };

  onSubmit = async () => {
    const { dispatch, quoteId, shipmentData, vehicleId, vehicleInfo, rulesEngineResults } = this.props;
    const {
      usedPricingEngine,
      askingForFeedback,
      automatedPricingIsSafe,
      usingSuggestedDealerPrice,
      usingSuggestedTransporterPrice,
      ranAutoPricing,
      taqSuccess,
      isLowPriceMargin,
    } = this.state;
    const shipmentId = shipmentData.shipmentId;

    let taqFeedbackPayload;
    let rulesEngineResultPayload;
    if (usedPricingEngine) {
      let pricingEngineFormResult;

      try {
        pricingEngineFormResult = await this.pricingEngineFeedBackFormRef.current.validateFields();
      } catch (err) {
        // if feedback form fails to validate return early
        return;
      }

      // feedback form validated, create payload for feedback POST request

      taqFeedbackPayload = {
        vehicleYear: vehicleInfo.year,
        vehicleMake: vehicleInfo.make,
        vehicleModel: vehicleInfo.model,
        // vehicleId will be added in dispatched redux action below

        userId: pricingEngineFormResult.userId,
        status: pricingEngineFormResult.status,
        isError: pricingEngineFormResult.isError,
        errors: pricingEngineFormResult.errors,
        message: pricingEngineFormResult.message,
        results: pricingEngineFormResult.results,
        adjustments: pricingEngineFormResult.adjustments,
        quoteId: pricingEngineFormResult.quoteId,
        encryptedQuoteId: pricingEngineFormResult.encryptedQuoteId,
        deposit: pricingEngineFormResult.deposit ? Number(pricingEngineFormResult.deposit) : null,
        total: pricingEngineFormResult.total,
        mileage: pricingEngineFormResult.mileage,
        // currently setting as false for all requests, may be used later
        followUp: false,
        notes: askingForFeedback ? pricingEngineFormResult.notes : "",
        dealerReason: askingForFeedback ? pricingEngineFormResult.dealerReason : null,
        transporterReason: askingForFeedback ? pricingEngineFormResult.transporterReason : null,
        transporterPrice: askingForFeedback ? pricingEngineFormResult.transporterPrice : null,
        dealerPrice: askingForFeedback ? pricingEngineFormResult.dealerPrice : null,
      };
    }

    const usedRulesEnginePrice =
      usingSuggestedDealerPrice && usingSuggestedTransporterPrice && automatedPricingIsSafe && taqSuccess;

    if (ranAutoPricing) {
      rulesEngineResultPayload = {
        // TAQResultId will get added in the action
        // QuoteId will get added in the action
        request: {
          Workflow: "ValidTAQRequest",
          RuleSet: "ValidTAQRequest.json",

          Parameters: {
            origin: shipmentData.pickup.location.zip,
            destination: shipmentData.dropoff.location.zip,
            shipperEntityId: shipmentData.entityId?.toUpperCase(),
            vin: vehicleInfo.vin,
          },
        },
        response: rulesEngineResults,
      };
    }

    this.quotesFormRef.current.validateFields().then(result => {
      if (isLowPriceMargin) {
        const lowPriceMarginReason = this?.lowPriceMarginFormRef?.current?.getFieldValue("lowPriceMarginReason");
        const lowPriceMarginReasonEntered =
          lowPriceMarginReason !== null && lowPriceMarginReason !== undefined && lowPriceMarginReason !== "";

        if (!lowPriceMarginReasonEntered) {
          this.setState({
            displayLowPriceMarginModal: true,
          });

          return;
        }

        result.lowPriceMarginReason = lowPriceMarginReason;
      }

      dispatch({
        type: `adminShipmentView/resolveQuote`,
        vehicleId,
        payload: {
          quoteId,
          ...result,
          quoteType: usedRulesEnginePrice ? "PRICING ENGINE" : "CUSTOM QUOTE",
        },
        shipmentId,
        taqFeedbackPayload,
        rulesEngineResultPayload,
      }).then(() => {
        this.onClose();
      });
    });
  };

  componentDidUpdate(prevProps) {
    if (this.props.submitting) return;

    // When drawer opens
    if (this.props.visible && !prevProps.visible) {
      this.setState({ displayPricingEngine: true });
    }
  }

  render() {
    const { submitting, visible, shipmentData, vehicleInfo, checkingAutomatedPricing } = this.props;
    const {
      isMobile,
      usedPricingEngine,
      usingSuggestedDealerPrice,
      usingSuggestedTransporterPrice,
      askingForFeedback,
      loadingPricingEngineResponse,
      automatedPricingIsSafe,
      displayPricingEngine,
      taqSuccess,
      displayLowPriceMarginModal,
      currentDealerPrice,
      currentTransporterPrice,
      isLowPriceMargin,
    } = this.state;

    return (
      <div>
        <Drawer
          title="Review and Create Quote"
          width={isMobile ? "80%" : "40%"}
          onClose={this.onClose}
          visible={visible}
          bodyStyle={{ paddingBottom: 80 }}
          footer={
            <Row align="space-between">
              <Col flex={1}>
                <MarginFeedbackAlert
                  dealerPrice={currentDealerPrice}
                  transporterPrice={currentTransporterPrice}
                  lowMarginAlertCallback={() => {
                    if (!isLowPriceMargin) {
                      this.setState({
                        isLowPriceMargin: true,
                      });
                    }
                  }}
                  goodMarginAlertCallback={() => {
                    if (isLowPriceMargin) {
                      this.setState({
                        isLowPriceMargin: false,
                      });
                    }
                  }}
                />
              </Col>
              <Col flex={3}>
                <div style={{ textAlign: "right" }}>
                  <Button onClick={this.onClose} style={{ marginRight: 8 }}>
                    Cancel
                  </Button>
                  <Button type="primary" onClick={this.onSubmit} loading={submitting}>
                    Save
                  </Button>
                </div>
              </Col>
            </Row>
          }
        >
          <Form ref={this.quotesFormRef} layout="vertical">
            <Form.Item name="dealerPrice" label="Dealer Price" rules={[{ required: true }]}>
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={value => value.replace(/\$\s?|(,*)/g, "")}
                value={this.state.currentDealerPrice}
                onChange={this.checkUpdatedPrices}
                onStep={this.checkUpdatedPrices}
                disabled={
                  (automatedPricingIsSafe && taqSuccess) || checkingAutomatedPricing || loadingPricingEngineResponse
                }
              />
            </Form.Item>
            <Form.Item name="transporterPrice" label="Transporter Price" rules={[{ required: true }]}>
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={value => value.replace(/\$\s?|(,*)/g, "")}
                value={this.state.currentTransporterPrice}
                onChange={this.checkUpdatedPrices}
                onStep={this.checkUpdatedPrices}
                disabled={
                  (automatedPricingIsSafe && taqSuccess) || checkingAutomatedPricing || loadingPricingEngineResponse
                }
              />
            </Form.Item>
          </Form>

          {displayPricingEngine && (
            <PricingEngineFeedbackForm
              vinValidated={true}
              usedPricingEngine={usedPricingEngine}
              askingForFeedback={askingForFeedback}
              usingSuggestedDealerPrice={usingSuggestedDealerPrice}
              usingSuggestedTransporterPrice={usingSuggestedTransporterPrice}
              checkUpdatedPrices={this.checkUpdatedPrices}
              vehicleShipmentData={shipmentData}
              vehicleInfo={vehicleInfo}
              setParentState={this.setComponentState}
              quotesFormRef={this.quotesFormRef}
              pricingEngineFeedBackFormRef={this.pricingEngineFeedBackFormRef}
              loadingPricingEngineResponse={loadingPricingEngineResponse}
              automatedPricingIsSafe={automatedPricingIsSafe}
              checkingAutomatedPricing={checkingAutomatedPricing}
              autoSubmitCallBack={this.onSubmit}
              displayPricingEngine={this.state.displayPricingEngine}
              taqSuccess={taqSuccess}
            />
          )}
        </Drawer>
        <Modal
          title="Low Pricing Margin"
          visible={displayLowPriceMarginModal}
          onOk={this.onSubmit}
          onCancel={() => {
            this.setState({
              displayLowPriceMarginModal: false,
            });
          }}
          confirmLoading={submitting}
          afterClose={() => {
            this.lowPriceMarginFormRef.current.resetFields();
          }}
        >
          <div style={{ marginBottom: 32 }}>Pricing margin is less than 15%. Please enter reason for low margin.</div>
          <Form ref={this.lowPriceMarginFormRef} layout="vertical">
            <Form.Item name="lowPriceMarginReason" label="Reason for Low Margin" rules={[{ required: true }]}>
              <TextArea rows={4} />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    );
  }
}

export default connect(({ loading, adminShipmentView }) => ({
  adminShipmentView,
  submitting: loading.effects["adminShipmentView/resolveQuote"],
  checkingAutomatedPricing: loading.effects["adminShipmentView/runRulesEngine"],
  rulesEngineResults: adminShipmentView.rulesEngineResults,
}))(CreateQuoteMenuItem);
